import React, { useState, useEffect, useRef, useLayoutEffect } from 'react';
import type { ResponsiveValue } from '@chakra-ui/react';
import { Box, Text, Input, HStack, VStack, Button, Popover, PopoverTrigger, PopoverContent, PopoverBody, PopoverArrow, PopoverCloseButton, Checkbox, Tag, TagLabel, TagCloseButton, Tabs, TabList, TabPanels, Tab, TabPanel, Flex, Select } from '@chakra-ui/react';
import PageTitle from '../components/PageTitle';
import { POSITIONS } from '../constants';
import { useMyLeagueStore } from '../store/myLeagueStore';
import { useUserContext } from '../contexts/UserContext';
import { useFreeAgents } from '../hooks/useFreeAgents';
import AvailablePlayersContent from '../components/AvailablePlayersContent';
import { Spinner, Center } from '@chakra-ui/react';

// Constants for gradient and frost wash (shared with AvailablePlayersContent)
const PAGE_GRADIENT = `linear-gradient(165deg, black 0%, #002e0b 80vh, #002e0b 100%)`;
const FROST_WASH = `inset 0 0 0 9999px rgba(255,255,255,0.06)`;

type Chip = { type: 'nameLike' | 'position' | 'salaryMin' | 'salaryMax' | 'status'; label: string; value?: string };

const FreeAgents: React.FC = () => {
  // Filter state
  const [nameLike, setNameLike] = useState('');
  const [selectedPositions, setSelectedPositions] = useState<string[]>([]);
  const [salaryMin, setSalaryMin] = useState<string>('');
  const [salaryMax, setSalaryMax] = useState<string>('');
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);
  const [positionPopoverOpen, setPositionPopoverOpen] = useState(false);
  const [statusPopoverOpen, setStatusPopoverOpen] = useState(false);
  const [viewMode, setViewMode] = useState<'all_players' | 'accepting_offers' | 'my_offers'>('all_players');
  const [isTabsSticky, setIsTabsSticky] = useState(false);

  // Ref for sticky tabs wrapper to measure height
  const stickyTabsRef = useRef<HTMLDivElement>(null);
  // Refs for sticky detection
  const lastStickyRef = useRef<boolean | null>(null);
  const initialYRef = useRef<number | null>(null);

  // Get current league and system settings
  const myLeague = useMyLeagueStore(state => state.myLeague);
  const leagueId = myLeague?.id;
  const { systemSettings, myTeam } = useUserContext();

  // Track current page and accumulated players
  const [currentPage, setCurrentPage] = React.useState(0);
  const [accumulatedPlayers, setAccumulatedPlayers] = React.useState<any[]>([]);
  const [totalPages, setTotalPages] = React.useState(0);
  const [faPayrollImpact, setFaPayrollImpact] = useState(0);

  // Map view mode to how we actually fetch from the API.
  // "My Offers" should reuse the same dataset as "Accepting Offers".
  const serverFilterMode: 'accepting_offers' | 'all_players' = viewMode === 'my_offers'
    ? 'accepting_offers'
    : viewMode === 'accepting_offers'
      ? 'accepting_offers'
      : 'all_players';

  // Create a stable filter key to detect filter changes
  const filterKey = React.useMemo(
    () =>
      JSON.stringify({
        nameLike,
        selectedPositions,
        salaryMin,
        salaryMax,
        selectedStatuses,
        serverFilterMode,
      }),
    [nameLike, selectedPositions, salaryMin, salaryMax, selectedStatuses, serverFilterMode]
  );

  // Reset accumulated players when filters change
  useEffect(() => {
    setCurrentPage(0);
    setAccumulatedPlayers([]);
    setTotalPages(0);
  }, [filterKey]);

  useEffect(() => {
    if (
      systemSettings?.seasonStatus &&
      (systemSettings.seasonStatus === 'Off Season' ||
        systemSettings.seasonStatus === 'In Season')
    ) {
      // When offers are allowed, default to "Accepting Offers"
      setViewMode('all_players');
    } else {
      // When offers are not allowed, default to "All Players"
      setViewMode('all_players');
    }
  }, [systemSettings?.seasonStatus]);

  // Measure sticky tabs height and update CSS variable
  useLayoutEffect(() => {
    const update = () => {
      const el = stickyTabsRef.current;
      if (!el) return;
      const h = Math.ceil(el.getBoundingClientRect().height);
      document.documentElement.style.setProperty('--sticky-tabs-height', `${h}px`);
    };

    update();

    const ro = new ResizeObserver(() => update());
    if (stickyTabsRef.current) ro.observe(stickyTabsRef.current);

    window.addEventListener('resize', update);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', update);
    };
  }, []);

  // Detect when tabs become sticky using bounding rect
  useEffect(() => {
    let rafId: number | null = null;

    const checkSticky = () => {
      if (rafId !== null) return;
      rafId = requestAnimationFrame(() => {
        rafId = null;
        const el = stickyTabsRef.current;
        if (!el) return;

        const rect = el.getBoundingClientRect();
        const rectTop = rect.top;
        const styleTop = window.getComputedStyle(el).top;
        const stickyTop = parseFloat(styleTop || '0') || 0;
        const clamped = Math.abs(rectTop - stickyTop) <= 1;

        // Capture initial position once
        if (initialYRef.current == null) {
          initialYRef.current = rectTop + window.scrollY;
        }

        const initialY = initialYRef.current ?? 0;
        const pastNatural = window.scrollY >= initialY - stickyTop - 1;
        const nextSticky = clamped && pastNatural;

        // Update state only when value changes
        if (lastStickyRef.current !== nextSticky) {
          lastStickyRef.current = nextSticky;
          setIsTabsSticky(nextSticky);
        }
      });
    };

    checkSticky();
    window.addEventListener('scroll', checkSticky, { passive: true });
    window.addEventListener('resize', checkSticky);
    return () => {
      window.removeEventListener('scroll', checkSticky);
      window.removeEventListener('resize', checkSticky);
      if (rafId !== null) cancelAnimationFrame(rafId);
    };
  }, []);

  // Sync sticky tabs gradient slice to match page background
  useEffect(() => {
    const syncStickyGradientSlice = () => {
      const stickyEls = document.querySelectorAll<HTMLElement>('[data-sticky-tabs-surface="1"]');

      if (stickyEls.length === 0) return;

      // Compute shared background canvas size
      const bgW = document.documentElement.clientWidth;
      const bgH = document.documentElement.scrollHeight;

      // Apply styles to each sticky tabs surface using CURRENT DOM positions
      stickyEls.forEach((el) => {
        const rect = el.getBoundingClientRect();
        const pageX = window.scrollX + rect.left;
        const pageY = window.scrollY + rect.top;

        // Apply inline styles directly
        el.style.backgroundColor = '#002e0b';
        el.style.backgroundImage = PAGE_GRADIENT;
        el.style.backgroundRepeat = 'no-repeat';
        el.style.backgroundSize = `${bgW}px ${bgH}px`;
        el.style.backgroundPosition = `${-pageX}px ${-pageY}px`;
        el.style.boxShadow = FROST_WASH;
      });
    };

    // Initial update after first paint
    const rafId = requestAnimationFrame(() => {
      syncStickyGradientSlice();
    });

    // Throttle with requestAnimationFrame
    let pendingRaf: number | null = null;
    const handleUpdate = () => {
      if (pendingRaf === null) {
        pendingRaf = requestAnimationFrame(() => {
          syncStickyGradientSlice();
          pendingRaf = null;
        });
      }
    };

    // Listen to window scroll
    window.addEventListener('scroll', handleUpdate, { passive: true });
    // Listen to window resize
    window.addEventListener('resize', handleUpdate, { passive: true });

    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener('scroll', handleUpdate);
      window.removeEventListener('resize', handleUpdate);
      if (pendingRaf !== null) {
        cancelAnimationFrame(pendingRaf);
      }
    };
  }, []); // Run once on mount

  // Load free agents with all filters (backend handles filtering)
  const {
    data: freeAgentsData,
    isLoading: freeAgentsLoading,
    error: freeAgentsError,
    refetch: refetchFreeAgents,
    isFetching,
  } = useFreeAgents({
    leagueId: leagueId || 0,
    filters: {
      nameLike: nameLike,
      positions: selectedPositions.length > 0 ? selectedPositions : undefined,
      contractMin: salaryMin || undefined,
      contractMax: salaryMax || undefined,
      statuses: selectedStatuses.length > 0 ? selectedStatuses : undefined,
      // Backend filter for players currently accepting offers
      acceptingOffers: serverFilterMode === 'accepting_offers',
    },
    page: currentPage,
    pageSize: 50,
    debounceMs: 300, // Debounce name search by 300ms
  });

  // Accumulate players as pages load
  useEffect(() => {
    if (freeAgentsData) {
      const newPlayers = freeAgentsData.players || [];
      setTotalPages(freeAgentsData.totalPages || 0);

      // Only accumulate if this data matches the current filter state
      // This prevents appending data from old queries after filters change
      if (currentPage === 0) {
        // First page - replace accumulated players
        setAccumulatedPlayers(newPlayers);
      } else if (currentPage > 0) {
        // Subsequent pages - append to accumulated players
        setAccumulatedPlayers(prev => {
          // Safety check: only append if we have existing players (prev.length > 0)
          // This ensures we're not appending to a reset state
          if (prev.length === 0) {
            return newPlayers; // If somehow we're on page > 0 but have no players, just set them
          }
          return [...prev, ...newPlayers];
        });
      }
    }
  }, [freeAgentsData, currentPage]);

  // Get players from accumulated list, applying "My Offers" client-side filter when selected
  // and sorting by expiration date (soonest expiring first) for accepting-offers views.
  const filteredPlayers = React.useMemo(() => {
    let list = accumulatedPlayers;

    if (viewMode === 'my_offers' && myTeam) {
      list = accumulatedPlayers.filter((entry: any) => {
        const hasMyOffer =
          entry.dealData &&
          Array.isArray(entry.dealData.offers) &&
          entry.dealData.offers.some(
            (offer: any) => Number(offer.team) === Number(myTeam)
          );

        const isMySettlementFA =
          entry.deal_status === 'settlement_free_agent_offer' &&
          entry.dealData &&
          Array.isArray(entry.dealData.teams) &&
          entry.dealData.teams.some(
            (teamId: any) => Number(teamId) === Number(myTeam)
          );

        return hasMyOffer || isMySettlementFA;
      });
    }

    // For accepting-offers views (Accepting Offers + My Offers),
    // sort by dealData.expires ascending (soonest expiration first).
    if (serverFilterMode === 'accepting_offers') {
      const sorted = [...list].sort((a: any, b: any) => {
        const aTime = a?.dealData?.expires ? new Date(a.dealData.expires).getTime() : Infinity;
        const bTime = b?.dealData?.expires ? new Date(b.dealData.expires).getTime() : Infinity;
        return aTime - bTime;
      });
      return sorted;
    }

    return list;
  }, [accumulatedPlayers, viewMode, myTeam, serverFilterMode]);

  // Calculate FA payroll impact: first-year salary only for (a) offers in settlement
  // where my team is involved, and (b) offers where I am the highest bidder.
  useEffect(() => {
    if (serverFilterMode !== 'accepting_offers' || !myTeam) {
      setFaPayrollImpact(0);
      return;
    }

    const total = accumulatedPlayers.reduce((sum, entry: any) => {
      const offers = entry?.dealData?.offers;
      if (!Array.isArray(offers) || offers.length === 0) return sum;

      const myOffer = offers.find(
        (offer: any) => Number(offer.team) === Number(myTeam)
      );
      if (!myOffer?.offer?.yearlySalaries?.length) return sum;

      const yearlySalaries = myOffer.offer.yearlySalaries;
      const firstYear = Number(yearlySalaries[0]);
      if (!Number.isFinite(firstYear) || firstYear <= 0) return sum;

      // Include if (a) in settlement and my team is in the deal
      const isSettlement = entry.deal_status === 'settlement_free_agent_offer';
      const myTeamInSettlement =
        isSettlement &&
        entry.dealData?.teams?.some(
          (teamId: any) => Number(teamId) === Number(myTeam)
        );
      if (myTeamInSettlement) return sum + firstYear;

      // Include if (b) I am the highest bidder (same logic as "Current High Bidder")
      const highestOfferScore = offers.reduce(
        (max: number, o: any) => Math.max(max, o.offer?.offerScore ?? 0),
        0
      );
      const myScore = myOffer.offer?.offerScore ?? 0;
      const isHighestBidder =
        highestOfferScore > 0 &&
        Math.abs(myScore - highestOfferScore) < 0.001;

      if (isHighestBidder) return sum + firstYear;
      return sum;
    }, 0);

    setFaPayrollImpact(total);
  }, [accumulatedPlayers, myTeam, serverFilterMode]);

  // Handle "Show More" click
  const handleShowMore = () => {
    if (currentPage < totalPages - 1) {
      setCurrentPage(prev => prev + 1);
    }
  };

  // Remove a filter chip
  const removeChip = (type: string, value?: string) => {
    if (type === 'nameLike') setNameLike('');
    if (type === 'position' && value) setSelectedPositions(selectedPositions.filter(p => p !== value));
    if (type === 'salaryMin') setSalaryMin('');
    if (type === 'salaryMax') setSalaryMax('');
    if (type === 'status' && value) setSelectedStatuses(selectedStatuses.filter(s => s !== value));
  };

  // Position multi-select logic
  const togglePosition = (pos: string) => {
    setSelectedPositions(prev => prev.includes(pos) ? prev.filter(p => p !== pos) : [...prev, pos]);
  };

  // Status multi-select logic
  const toggleStatus = (status: string) => {
    setSelectedStatuses(prev => prev.includes(status) ? prev.filter(s => s !== status) : [...prev, status]);
  };

  // Format salary with commas for chips and display
  const formatWithCommas = (val: string) => {
    const num = Number(val.replace(/,/g, ''));
    if (!val || isNaN(num)) return '';
    return num.toLocaleString();
  };

  // Render filter chips
  const chips: Chip[] = [
    nameLike ? { type: 'nameLike', label: `Name: ${nameLike}` } : undefined,
    ...selectedPositions.map(pos => ({ type: 'position', label: POSITIONS.find(p => p.value === pos)?.label || pos, value: pos })),
    salaryMin ? { type: 'salaryMin', label: `Min Salary: $${formatWithCommas(salaryMin)}` } : undefined,
    salaryMax ? { type: 'salaryMax', label: `Max Salary: $${formatWithCommas(salaryMax)}` } : undefined,
    ...selectedStatuses.map(status => ({ type: 'status', label: status === 'has_offers' ? 'Has Offers' : 'No Offers', value: status })),
  ].filter((chip): chip is Chip => Boolean(chip));

  const ViewModeSelector = ({ display }: { display?: ResponsiveValue<string> }) => (
    <HStack spacing={2} align="center" ml="auto" display={display}>
      <Select
        size="sm"
        width="190px"
        value={viewMode}
        onChange={(e) =>
          setViewMode(e.target.value as 'all_players' | 'accepting_offers' | 'my_offers')
        }
        bg="whiteAlpha.100"
        borderColor="whiteAlpha.300"
        color="white"
        _hover={{ borderColor: 'green.300' }}
        _focus={{ borderColor: 'green.300', boxShadow: '0 0 0 1px rgba(72,187,120,0.8)' }}
      >
        <option value="all_players">All Players</option>
        <option value="accepting_offers">Current Offers</option>
        <option value="my_offers">My Offers</option>
      </Select>
    </HStack>
  );

  return (
    <Box className="minimal-panel-padding">
      <Flex align="center" justify="space-between" flexWrap="wrap" gap={2} mb={4}>
        <Box>
          <PageTitle mb={0}>Free Agents</PageTitle>
        </Box>
        <ViewModeSelector display={{ base: 'flex', md: 'none' }} />
      </Flex>
      {/* Filter Bar */}
      <VStack align="flex-start" spacing={2} mb={2} width="100%">
        <Flex align="center" justify="space-between" flexWrap="wrap" width="100%">
          <HStack spacing={4} flexWrap="wrap">
            <ViewModeSelector display={{ base: 'none', md: 'flex' }} />
            {/* Name Like */}
            <Input
              placeholder="Search by name..."
              value={nameLike}
              onChange={e => setNameLike(e.target.value)}
              width="160px"
              bg="whiteAlpha.100"
              color="white"
              _placeholder={{ color: 'gray.400' }}
            />
            {/* Position Multi-Select */}
            <Popover
              isOpen={positionPopoverOpen}
              onClose={() => setPositionPopoverOpen(false)}
              placement="bottom-start"
            >
              <PopoverTrigger>
                <Button onClick={() => setPositionPopoverOpen(!positionPopoverOpen)} variant="outline" colorScheme="blue">
                  {selectedPositions.length > 0 ? `${selectedPositions.length} Position${selectedPositions.length > 1 ? 's' : ''}` : 'Select Positions'}
                </Button>
              </PopoverTrigger>
              <PopoverContent bg="gray.800" color="white" minW="200px">
                <PopoverArrow />
                <PopoverCloseButton />
                <PopoverBody>
                  <VStack align="flex-start" spacing={1}>
                    {POSITIONS.map(pos => (
                      <Checkbox
                        key={pos.value}
                        isChecked={selectedPositions.includes(pos.value)}
                        onChange={() => togglePosition(pos.value)}
                        colorScheme="blue"
                      >
                        {pos.label}
                      </Checkbox>
                    ))}
                  </VStack>
                </PopoverBody>
              </PopoverContent>
            </Popover>
            {/* Salary Range */}
            {/* <HStack spacing={1}>
            <InputGroup>
              <InputLeftElement pointerEvents="none" color="gray.400" fontSize="md" children="$" />
              <NumberInput
                value={formatWithCommas(salaryMin)}
                onChange={val => setSalaryMin(val.replace(/,/g, ''))}
                min={0}
                width="170px"
                bg="whiteAlpha.100"
                color="white"
              >
                <NumberInputField placeholder="Min Salary" _placeholder={{ color: 'gray.400' }} pl={6} />
              </NumberInput>
            </InputGroup>
            <Text color="gray.400">-</Text>
            <InputGroup>
              <InputLeftElement pointerEvents="none" color="gray.400" fontSize="md" children="$" />
              <NumberInput
                value={formatWithCommas(salaryMax)}
                onChange={val => setSalaryMax(val.replace(/,/g, ''))}
                min={0}
                width="170px"
                bg="whiteAlpha.100"
                color="white"
              >
                <NumberInputField placeholder="Max Salary" _placeholder={{ color: 'gray.400' }} pl={6} />
              </NumberInput>
            </InputGroup>
          </HStack> */}
            {/* Status Multi-Select */}
            <Popover
              isOpen={statusPopoverOpen}
              onClose={() => setStatusPopoverOpen(false)}
              placement="bottom-start"
            >
              <PopoverTrigger>
                <Button onClick={() => setStatusPopoverOpen(!statusPopoverOpen)} variant="outline" colorScheme="blue">
                  {selectedStatuses.length > 0 ? `${selectedStatuses.length} Status${selectedStatuses.length > 1 ? 'es' : ''}` : 'Select Status'}
                </Button>
              </PopoverTrigger>
              <PopoverContent bg="gray.800" color="white" minW="200px">
                <PopoverArrow />
                <PopoverCloseButton />
                <PopoverBody>
                  <VStack align="flex-start" spacing={1}>
                    <Checkbox
                      isChecked={selectedStatuses.includes('has_offers')}
                      onChange={() => toggleStatus('has_offers')}
                      colorScheme="blue"
                    >
                      Has Offers
                    </Checkbox>
                    <Checkbox
                      isChecked={selectedStatuses.includes('no_offers')}
                      onChange={() => toggleStatus('no_offers')}
                      colorScheme="blue"
                    >
                      No Offers
                    </Checkbox>
                  </VStack>
                </PopoverBody>
              </PopoverContent>
            </Popover>
          </HStack>
        </Flex>
        {/* Filter Chips + FA payroll impact (separate row under filters) */}
        <Flex align="center" flexWrap="wrap">
          <HStack spacing={2} flexWrap="wrap">
            {chips.map((chip, idx) => (
              <Tag key={idx} size="md" colorScheme="blue" borderRadius="full">
                <TagLabel>{chip.label}</TagLabel>
                <TagCloseButton onClick={() => removeChip(chip.type, chip.value)} />
              </Tag>
            ))}
          </HStack>
          {/* {faPayrollImpact > 0 && (
            <Box ml="auto" mt={{ base: 2, md: 0 }}>
              <Text fontSize="sm" fontWeight="bold" color="gray.200">
                PotentialPayroll Impact: ${faPayrollImpact.toLocaleString()}
              </Text>
            </Box>
          )} */}
        </Flex>
      </VStack>
      {/* Tabs (same style as RosterPage) */}
      <Tabs
        colorScheme="blue"
        variant="enclosed"
        mt={4}
        onChange={() => {
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              window.dispatchEvent(new Event('scroll'));
            });
          });
        }}
      >
        <Box
          ref={stickyTabsRef}
          position="sticky"
          top="0"
          zIndex={40}
          data-sticky-tabs-surface="1"
          {...(isTabsSticky ? { 'data-tabs-stuck': '1' } : {})}
        >
          <TabList px={0} py={0} mb={0} bg="transparent">
            <Tab sx={{ borderBottomRadius: 0 }}>Players</Tab>
            <Tab sx={{ borderBottomRadius: 0 }}>Additional Info</Tab>
            <Tab sx={{ borderBottomRadius: 0 }}>Contract Details</Tab>
            {faPayrollImpact > 0 && systemSettings?.seasonStatus !== 'In Season' && (
              <Text

                ml={4}
                mt={1}
                pt={'7px'}
                fontSize="sm"
                fontStyle="italic"
                fontWeight="bold"
                color="gray.200"
                fontFamily="system-ui"

              >
                Potential Payroll Impact: ${faPayrollImpact.toLocaleString()}
              </Text>
            )}
          </TabList>
        </Box>
        <TabPanels>
          <TabPanel p={0}>
            {freeAgentsLoading && currentPage === 0 ? (
              <Center py={10}><Spinner size="xl" /></Center>
            ) : freeAgentsError ? (
              <Text color="red.400">Error loading free agents.</Text>
            ) : leagueId ? (
              <>
                <AvailablePlayersContent
                  tabName="Players"
                  players={filteredPlayers}
                  refetch={refetchFreeAgents}
                  isFreeAgentView={true}
                  systemSettings={systemSettings}
                  viewAcceptingOffers={viewMode === 'accepting_offers'}
                />
                {totalPages > 0 && currentPage < totalPages - 1 && (
                  <Flex justify="center" mt={4}>
                    <Button
                      onClick={handleShowMore}
                      isLoading={isFetching}
                      colorScheme="blue"
                    >
                      Load More
                    </Button>
                  </Flex>
                )}
              </>
            ) : (
              <Text color="gray.400">Select a league to view free agents.</Text>
            )}
          </TabPanel>
          <TabPanel p={0}>
            {freeAgentsLoading && currentPage === 0 ? (
              <Center py={10}><Spinner size="xl" /></Center>
            ) : freeAgentsError ? (
              <Text color="red.400">Error loading free agents.</Text>
            ) : leagueId ? (
              <>
                <AvailablePlayersContent
                  tabName="Additional Info"
                  players={filteredPlayers}
                  refetch={refetchFreeAgents}
                  isFreeAgentView={true}
                  systemSettings={systemSettings}
                />
                {totalPages > 0 && currentPage < totalPages - 1 && (
                  <Flex justify="center" mt={4}>
                    <Button
                      onClick={handleShowMore}
                      isLoading={isFetching}
                      colorScheme="blue"
                    >
                      Load More
                    </Button>
                  </Flex>
                )}
              </>
            ) : (
              <Text color="gray.400">Select a league to view free agents.</Text>
            )}
          </TabPanel>
          <TabPanel p={0}>
            {freeAgentsLoading && currentPage === 0 ? (
              <Center py={10}><Spinner size="xl" /></Center>
            ) : freeAgentsError ? (
              <Text color="red.400">Error loading free agents.</Text>
            ) : leagueId ? (
              <>
                <AvailablePlayersContent
                  tabName="Contract Details"
                  players={filteredPlayers}
                  refetch={refetchFreeAgents}
                  isFreeAgentView={true}
                  systemSettings={systemSettings}
                />
                {totalPages > 0 && currentPage < totalPages - 1 && (
                  <Flex justify="center" mt={4}>
                    <Button
                      onClick={handleShowMore}
                      isLoading={isFetching}
                      colorScheme="blue"
                    >
                      Load More
                    </Button>
                  </Flex>
                )}
              </>
            ) : (
              <Text color="gray.400">Select a league to view free agents.</Text>
            )}
          </TabPanel>
        </TabPanels>
      </Tabs>
    </Box>
  );
};

export default FreeAgents; 